/**
 * Midwest craft malthouses — Great Lakes Malting, Maltwerks, Mitten State Malt,
 * Origin Malt, Rustic Brew Farm, Sugar Creek Malt Co., Two Track Malting and
 * ND Malting and Hops.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** Upper Midwest, Ohio, Indiana and North Dakota craft malts. @see Fermentable */
export const midwestCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Great Lakes Malting — Traverse City, Michigan
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'great-lakes-lake-michigan-pale',
    name: 'Lake Michigan Pale',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 2.5,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'The house pale at 2–3 SRM: biscuit, frosted flakes and honey. A single-infusion base for any pale beer.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-superior-pilsner',
    name: 'Lake Superior Pilsner',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 1.7,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their pilsner malt at 1.5–2 SRM: clean, with sweet grass and a melon note. The base of the signature range.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-white-wheat',
    name: 'Malted White Wheat',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      "Brioche, water cracker and baker's dough, with the head retention wheat always brings.",
    tags: ['wheat', 'white-wheat', 'foam', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-erie-caramel-20',
    name: 'Lake Erie Caramel 20',
    maker: 'Great Lakes Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    provenance: 'spec-sheet',
    description:
      'The lightest Lake Erie caramel: butterscotch, candied cherry and a cappuccino note.',
    tags: ['crystal', 'caramel', 'light', 'butterscotch', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-huron-munich-dark',
    name: 'Lake Huron Munich (Dark)',
    maker: 'Great Lakes Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'The darker of the two Lake Hurons at 7–15 SRM: bread crust, sweet roll and brown sugar, with the depth a dunkel wants.',
    tags: ['kilned', 'munich', 'dark', 'malty', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-ontario-vienna',
    name: 'Lake Ontario Vienna',
    maker: 'Great Lakes Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Toast, pie crust and almond at 3–5 SRM, with enough enzyme to carry most of a grist.',
    tags: ['kilned', 'vienna', 'amber', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-huron-munich-light',
    name: 'Lake Huron Munich (Light)',
    maker: 'Great Lakes Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 35,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'The lighter Lake Huron at 5–7 SRM: the same bread crust, sweet roll and brown sugar as its darker twin, at a color that still lets a beer read gold.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-erie-caramel-40',
    name: 'Lake Erie Caramel 40',
    maker: 'Great Lakes Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Toffee, toasted marshmallow and date. The middle grade and the most generally useful of the four.',
    tags: ['crystal', 'caramel', 'toffee', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-erie-caramel-60',
    name: 'Lake Erie Caramel 60',
    maker: 'Great Lakes Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      "Brown sugar, candied pecan and something distinctly of Werther's. Where a brown ale gets its sweetness.",
    tags: ['crystal', 'caramel', 'brown-ale', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-lake-erie-caramel-80',
    name: 'Lake Erie Caramel 80',
    maker: 'Great Lakes Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Burnt sugar and dried fruit with a slight roast at the finish. The dark end of the Lake Erie range.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-red-wheat',
    name: 'Malted Red Wheat',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Hazelnut, dried apricot and shortbread — fuller and nuttier than the white wheat, and the better of the two in a dunkelweizen.',
    tags: ['wheat', 'red-wheat', 'nutty', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-oats',
    name: 'Malted Oats',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Oatmeal, luscious and creamy, and their own three words for it. Body for a stout or a hazy IPA.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-rye',
    name: 'Malted Rye',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Bran, anise and an earthiness underneath. Rice hulls above a quarter of the grist.',
    tags: ['rye', 'spice', 'earthy', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-spelt',
    name: 'Malted Spelt',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Pecan, nutty, with a baking-spice lift. Spelt is an ancient hulled wheat and lauters better than malted wheat does, which is much of why Belgian brewers use it.',
    tags: ['spelt', 'ancient-grain', 'wheat', 'belgian', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-malted-corn',
    name: 'Malted Corn',
    maker: 'Great Lakes Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 10,
    colorLovibond: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Nutty, with roasted tortilla and corn nuts. Malted rather than flaked, so it tastes of corn rather than of nothing.',
    tags: ['corn', 'maize', 'adjunct', 'gluten-free', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-unmalted-wheat',
    name: 'Unmalted Wheat',
    maker: 'Great Lakes Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 10,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Raw wheat: haze, protein and a bready dryness. What a witbier and a hazy IPA are built on.',
    tags: ['wheat', 'unmalted', 'adjunct', 'witbier', 'hazy', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-distillers',
    name: 'Distiller’s Malt',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'High enzymatic power for whiskey, and useful in any beer whose grist is short of enzyme.',
    tags: ['base', 'distilling', 'high-dp', 'craft-malt', 'michigan'],
  }),
  craftMalt({
    id: 'great-lakes-chit',
    name: 'Chit Malt',
    maker: 'Great Lakes Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Foam and body, and an alternative to a dextrin malt. Barley stopped days into germination, so there is protein and almost no extract.',
    tags: ['chit', 'head-retention', 'body', 'craft-malt', 'michigan'],
  }),

  // ---------------------------------------------------------------------------
  // Maltwerks — Detroit Lakes, Minnesota
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'maltwerks-pale',
    name: 'Pale Malt',
    maker: 'Maltwerks',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Upper Midwest 2-row pale from Detroit Lakes. Clean, lightly sweet and enzyme-rich enough to convert wheat, rye or a modest corn fraction alongside it.',
    tags: ['base', 'two-row', 'craft-malt', 'minnesota', 'local'],
  }),
  craftMalt({
    id: 'maltwerks-wheat',
    name: 'Wheat Malt',
    maker: 'Maltwerks',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted Upper Midwest wheat for weissbier, wit and hazy IPA. Soft body and lasting foam; rice hulls help once wheat is a large fraction of the mash.',
    tags: ['base', 'wheat', 'craft-malt', 'minnesota', 'head-retention'],
  }),
  craftMalt({
    id: 'maltwerks-rye',
    name: 'Rye Malt',
    maker: 'Maltwerks',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Minnesota rye malt with a spicy cracker bite. Built for rye ale and rye whiskey mash bills; hulls keep the mash from sealing at rye-beer rates.',
    tags: ['base', 'rye', 'craft-malt', 'minnesota', 'spicy'],
  }),
  craftMalt({
    id: 'maltwerks-pilsner',
    name: 'Pilsner Malt',
    maker: 'Maltwerks',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lightly kilned Upper Midwest pilsner. Pale, clean and self-converting, the lager canvas from a house that also malts wheat and rye for the same regional brewers.',
    tags: ['base', 'pilsner', 'craft-malt', 'minnesota', 'local'],
  }),
  craftMalt({
    id: 'maltwerks-vienna',
    name: 'Vienna Malt',
    maker: 'Maltwerks',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Middle of the road between a pale ale malt and a Munich: golden-orange with a distinct toast and biscuit aroma, and enough enzyme to be most of a grist.',
    tags: ['kilned', 'vienna', 'amber', 'craft-malt'],
  }),
  craftMalt({
    id: 'maltwerks-munich',
    name: 'Munich Malt',
    maker: 'Maltwerks',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Dark in the sack and lightly toasted in the nose. For maltier beers that want character rather than color alone.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt'],
  }),
  craftMalt({
    id: 'maltwerks-aromatic',
    name: 'Aromatic Malt',
    maker: 'Maltwerks',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A pronounced malt aroma at 20 °L, past Munich and short of a crystal. Five to ten percent deepens the malt character of an amber or a bock without sweetening it.',
    tags: ['kilned', 'aromatic', 'malty', 'craft-malt'],
  }),

  // ---------------------------------------------------------------------------
  // Mitten State Malt — Mason, Michigan
  //
  // **These four names are our descriptors, not their products.** Mitten State
  // publish nothing: `mittenstatemalt.com` has been a stock WordPress install
  // with a single "Hello world!" post since 2017, and the Craft Maltsters Guild
  // finder lists them by name and location only. Violetta Pils below is the one
  // product name published anywhere, via the 2026 Malt Cup. That the other four
  // grains exist is corroborated — MSU's malting-barley programme describes them
  // malting "locally grown barley, wheat, rye, and oats" — but what is printed on
  // the sack, nobody outside Michigan knows.
  //
  // Their prose used to claim floor-malting and estate grain. Neither is
  // sourceable to anything: the only assertions of floor-malting are
  // search-engine summaries, and those same summaries hand Great Lakes Malting's
  // product line to Mitten State, which the underlying article never does. Both
  // claims are removed rather than hedged. The local-grain angle stays because
  // MSU do document it, and their contact Larry Judge farms the barley plots,
  // but "estate" is a stronger word than anything published supports.
  //
  // The malthouse is in Mason. Okemos is the registered business address, and
  // the rye's copy had it malted there.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'mitten-state-violetta-pils',
    name: 'Violetta Pils',
    maker: 'Mitten State Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their pilsner malt, named for the Violetta two-row it is made from and sold by crop year — the ’23 lot took silver in the Pilsen class at the 2026 Craft Malt Cup. Old Nation build a German-style pils on it as the whole grist, which is the job it is meant for.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'michigan', 'violetta'],
  }),
  craftMalt({
    id: 'mitten-state-pale',
    name: 'Pale Malt',
    maker: 'Mitten State Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Michigan-grown two-row malted in small batches. Rounder than commodity 2-row, and it will carry a whole ale grist on its own.',
    tags: ['base', 'ale', 'craft-malt', 'michigan', 'local'],
  }),
  craftMalt({
    id: 'mitten-state-wheat',
    name: 'Wheat Malt',
    maker: 'Mitten State Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Michigan-grown wheat malted in small batches. Foam, silk and a soft grain flavor for wheat beers and hazy ales; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'michigan', 'local', 'head-retention'],
  }),
  craftMalt({
    id: 'mitten-state-rye',
    name: 'Rye Malt',
    maker: 'Mitten State Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Michigan-grown rye malted in Mason. Peppery spice for rye pale ale and Michigan rye whiskey; sticky mash behavior is the tax on that character.',
    tags: ['base', 'rye', 'craft-malt', 'michigan', 'local', 'spicy'],
  }),
  craftMalt({
    id: 'mitten-state-oat',
    name: 'Oat Malt',
    maker: 'Mitten State Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 2.8,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Michigan-grown oats taken through a full malting. Creamy weight for hazy IPA and stout with less gumminess than flakes.',
    tags: ['adjunct', 'oats', 'craft-malt', 'michigan', 'local', 'mouthfeel'],
  }),

  // ---------------------------------------------------------------------------
  // Origin Malt — Marysville, Ohio
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'origin-pilsen',
    name: 'Pilsen Malt',
    maker: 'Origin Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://originmalt.com/product/pilsen-malt/',
    provenance: 'spec-sheet',
    description:
      'Midwest-grown Puffin winter 2-row, a Maris Otter descendant, kilned pale for cracker and almond aromatics. Regional-scale pilsner malt with commodity-level enzyme and a quieter terroir than floor-malted craft lots.',
    tags: ['base', 'pilsner', 'craft-malt', 'ohio', 'puffin', 'winter-barley'],
  }),
  craftMalt({
    id: 'origin-brewers',
    name: 'Brewers Malt',
    maker: 'Origin Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    colorLovibond: 2,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://originmalt.com/product/brewers-malt/',
    provenance: 'spec-sheet',
    description:
      'Well-modified Puffin 2-row with cereal-grain sweetness and toasty cracker aroma. The all-purpose Midwest base — pale enough for lager, rich enough for ale.',
    tags: ['base', 'two-row', 'craft-malt', 'ohio', 'puffin', 'winter-barley'],
  }),
  craftMalt({
    id: 'origin-light-munich',
    name: 'Light Munich',
    maker: 'Origin Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    url: 'https://originmalt.com/product/light-munich/',
    provenance: 'spec-sheet',
    description:
      'Puffin 2-row kilned to a warm toasted-nut character while keeping conversion power. Lighter than a classic Munich, so it can be a large fraction of a festbier without turning the beer brown.',
    tags: ['kilned', 'munich', 'craft-malt', 'ohio', 'puffin'],
  }),
  craftMalt({
    id: 'origin-c40',
    name: 'C40',
    maker: 'Origin Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    // Origin publish 45 for this one, despite the name.
    colorLovibond: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    url: 'https://originmalt.com/product/c40/',
    provenance: 'spec-sheet',
    description:
      'Puffin crystal with sweetened black-tea, caramel and toffee notes. Five to ten percent rounds a pale ale or ambers a Midwest lager.',
    tags: ['crystal', 'caramel', 'craft-malt', 'ohio', 'puffin'],
  }),
  craftMalt({
    id: 'origin-c60',
    name: 'C60',
    maker: 'Origin Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    url: 'https://originmalt.com/product/c60-malt/',
    provenance: 'spec-sheet',
    description:
      'Mid-color Puffin crystal with roasted raisin and light cocoa. The default crystal grade for amber and brown ales at 5–10 %.',
    tags: ['crystal', 'caramel', 'craft-malt', 'ohio', 'puffin'],
  }),
  craftMalt({
    id: 'origin-c90',
    name: 'C90',
    maker: 'Origin Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 3.6,
    colorLovibond: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    url: 'https://originmalt.com/product/c90/',
    provenance: 'spec-sheet',
    description:
      'Dark Puffin crystal: robust cocoa, roasted prune and raisin. A little goes a long way in brown ale, porter and stronger amber beers.',
    tags: ['crystal', 'caramel', 'craft-malt', 'ohio', 'puffin'],
  }),

  // ---------------------------------------------------------------------------
  // Rustic Brew Farm — Marysville, Ohio
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'rustic-brew-pilsner',
    name: 'Pilsner Malt',
    maker: 'Rustic Brew Farm',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 1.8,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://www.rusticbrewfarm.com/store/Pilsner-Malt-50lb-p257867685',
    provenance: 'spec-sheet',
    description:
      'Estate 2-row grown and malted on the Marysville farm. Light golden, sweet-malt aroma, and high diastatic power for a single-origin lager or pale ale.',
    tags: ['base', 'pilsner', 'craft-malt', 'ohio', 'estate', 'single-farm'],
  }),
  craftMalt({
    id: 'rustic-brew-pale',
    name: 'Pale Malt',
    maker: 'Rustic Brew Farm',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 2.3,
    diastaticPowerLintner: 144,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://www.rusticbrewfarm.com/store/Pale-Malt-50lb-p257857853',
    provenance: 'spec-sheet',
    description:
      'The farm’s most-used base: lightly kilned estate 2-row with a bready aroma and notable malt flavor. High enzyme, so it will convert wheat, rye or corn grown on the same ground.',
    tags: ['base', 'two-row', 'craft-malt', 'ohio', 'estate', 'high-enzyme'],
  }),
  craftMalt({
    id: 'rustic-brew-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Rustic Brew Farm',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82.7,
    moisturePercent: 3,
    colorLovibond: 3.5,
    diastaticPowerLintner: 92,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Kilned a step darker than the pale, trading some enzyme for a deeper malt taste. Still comfortably self-converting, the estate bitter and IPA base.',
    tags: ['base', 'pale-ale', 'craft-malt', 'ohio', 'estate'],
  }),
  craftMalt({
    id: 'rustic-brew-vienna',
    name: 'Vienna Malt',
    maker: 'Rustic Brew Farm',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 2.4,
    colorLovibond: 7,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Estate Vienna with biscuit and lightly toasted bread, copper color, and enough enzyme to convert a whole grist. Unusual dryness from the low shipped moisture.',
    tags: ['kilned', 'vienna', 'craft-malt', 'ohio', 'estate'],
  }),
  craftMalt({
    id: 'rustic-brew-munich-10',
    name: 'Munich 10',
    maker: 'Rustic Brew Farm',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 2.4,
    colorLovibond: 10,
    diastaticPowerLintner: 26,
    suggestedMaxPercent: 40,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Medium-heavy kilned estate Munich: strong bread aroma, biscuit and a light roast. Enzyme is limited, so keep a higher-DP base alongside it past about 40 %.',
    tags: ['kilned', 'munich', 'craft-malt', 'ohio', 'estate'],
  }),
  craftMalt({
    id: 'rustic-brew-wheat',
    name: 'Wheat Malt',
    maker: 'Rustic Brew Farm',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 4.8,
    colorLovibond: 2.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    url: 'https://www.rusticbrewfarm.com/store/Wheat-malt-50lb-p257867730',
    provenance: 'spec-sheet',
    description:
      'Soft red winter wheat grown and malted on the farm. Soft, crisp flavor with high extract; builds body and haze in wheat beers and hazy ales.',
    tags: ['base', 'wheat', 'craft-malt', 'ohio', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'rustic-brew-corn',
    name: 'Corn Malt',
    maker: 'Rustic Brew Farm',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 72.7,
    moisturePercent: 4.8,
    colorLovibond: 2.8,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    url: 'https://www.rusticbrewfarm.com/store/Corn-malt-50lb-p257857929',
    provenance: 'spec-sheet',
    description:
      'Estate non-GMO maize, malted as a stand-in for flaked corn. Adds body and a little color to light lager and cream ale without a cereal cooker.',
    tags: ['adjunct', 'corn', 'craft-malt', 'ohio', 'estate', 'pre-prohibition'],
  }),
  craftMalt({
    id: 'rustic-brew-rye',
    name: 'Rye Malt',
    maker: 'Rustic Brew Farm',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Rye grown, malted and packed on the same Marysville farm. Spicy adjunct for rye beer or rye whiskey; hulls keep the mash open at high rates.',
    tags: ['base', 'rye', 'craft-malt', 'ohio', 'estate', 'spicy'],
    url: 'https://www.rusticbrewfarm.com/store/Rye-malt-50lb-p257867837',
  }),
  craftMalt({
    id: 'rustic-brew-munich-30',
    name: 'Munich 30 Malt',
    maker: 'Rustic Brew Farm',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 30,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Kilned hotter than their Munich 10, at 30 SRM: more color and a deeper aroma, and past the point where much enzyme survives. A depth malt for dark lagers.',
    tags: ['kilned', 'munich', 'dark', 'malty', 'craft-malt', 'estate', 'farm-malted'],
  }),
  craftMalt({
    id: 'rustic-brew-carapils',
    name: 'CaraPils',
    maker: 'Rustic Brew Farm',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'A caramel malt at 2.6 SRM, so it sweetens and builds body while leaving the color alone. The dextrin malt of the range.',
    tags: ['crystal', 'carapils', 'dextrin', 'body', 'foam', 'craft-malt', 'estate', 'farm-malted'],
  }),

  // ---------------------------------------------------------------------------
  // Sugar Creek Malt Co. — Lebanon, Indiana
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'sugar-creek-pilsner',
    name: 'Pilsner Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Indiana 2-row pilsner from neighboring Midwest farms. Pale, clean and self-converting; the quiet lager base under a house famous for smoked and heritage malts.',
    tags: ['base', 'pilsner', 'craft-malt', 'indiana', 'floor-malted', 'local'],
    url: 'http://www.sugarcreekmalt.com/store/p15/PILSNER.html',
  }),
  craftMalt({
    id: 'sugar-creek-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-influenced Midwest pale ale malt. Bready enough to carry an IPA, enzyme-rich enough to convert a modest adjunct fraction.',
    tags: ['base', 'pale-ale', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p16/PALE_ALE.html',
  }),
  craftMalt({
    id: 'sugar-creek-ye-olde-pale',
    name: 'Ye Olde Pale Ale',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'English-style pale from Indiana barley, kilned for biscuit and toast. The house Maris Otter stand-in for bitter, mild and malt-forward IPA.',
    tags: ['base', 'pale-ale', 'craft-malt', 'indiana', 'english-style', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p17/YE_OLDE_PALE_ALE.html',
  }),
  craftMalt({
    id: 'sugar-creek-6-row-pre-prohibition',
    name: '6-Row Pre-Prohibition',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Six-row malted the way pre-Prohibition American lagers were built: grainy, high-enzyme and ready to convert a serious corn or rice fraction.',
    tags: ['base', 'six-row', 'craft-malt', 'indiana', 'high-enzyme', 'pre-prohibition'],
    url: 'http://www.sugarcreekmalt.com/store/p20/6-ROW_PRE-PROHIBITION.html',
  }),
  craftMalt({
    id: 'sugar-creek-munich',
    name: 'Munich Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Indiana Munich with bread-crust sweetness and enough enzyme to convert itself. The malt depth in festbier, dunkel and darker Midwest ales.',
    tags: ['kilned', 'munich', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p19/MUNICH.html',
  }),
  craftMalt({
    id: 'sugar-creek-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted white wheat from Indiana and neighboring farms. Soft body and lasting foam for wheat beers and hazy ales; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'indiana', 'head-retention'],
    url: 'http://www.sugarcreekmalt.com/store/p22/WHITE_WHEAT_MALT.html',
  }),
  craftMalt({
    id: 'sugar-creek-stjordal-smoked',
    name: 'Stjordal Smoked Pale',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Nordic-inspired pale smoked in the Stjørdal tradition — alder and juniper rather than beech. Can be the whole grist of a smoked farmhouse ale or a 5–20 % accent.',
    tags: ['smoked', 'nordic', 'craft-malt', 'indiana', 'floor-malted', 'base'],
    url: 'http://www.sugarcreekmalt.com/store/p57/STJORDAL.html',
  }),
  craftMalt({
    id: 'sugar-creek-hickory-smoked',
    name: 'Hickory Smoked Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Base malt smoked over hickory: strong, sweet-tangy smoke with a phenolic edge. A little goes a long way; 5–15 % seasons a porter or smoked lager without turning it into barbecue.',
    tags: ['smoked', 'hickory', 'craft-malt', 'indiana', 'floor-malted', 'base'],
  }),
  craftMalt({
    id: 'sugar-creek-2-row',
    name: '2 Row',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house two-row base: Indiana barley, floor-malted and kilned pale. Cleaner and more neutral than Ye Olde Pale, and the one to build on when the specialty malts are doing the talking.',
    tags: ['base', 'ale', 'pale', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-english-pale-ale',
    name: 'English Pale Ale',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted to an English spec: biscuit and light nut, lower in enzyme and richer in flavor than the standard two-row. What a bitter or a mild wants underneath it.',
    tags: ['base', 'ale', 'english', 'bitter', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-vienna',
    name: 'Vienna',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Indiana Vienna, floor-malted: toasted bread and a faint honey sweetness, with enough diastatic power to make up most of a grist. The bridge between the pale malts and Munich.',
    tags: ['kilned', 'vienna', 'lager', 'amber', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p18/VIENNA.html',
  }),
  craftMalt({
    id: 'sugar-creek-oat-malt',
    name: 'Oat Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted oats rather than flaked: the silky body oats are used for, with some enzyme of their own and far less of the gumminess flakes bring. Still worth rice hulls above 20 %.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p24/MALTED_OATS.html',
  }),
  craftMalt({
    id: 'sugar-creek-chit-malt',
    name: 'Chit Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Barley stopped a day or two into germination. Almost no color and little extract, added for the protein that builds head and body — a foam fix rather than a flavor one.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-crystal-20',
    name: 'Crystal 20',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The lightest of the house crystals: dark gold, light caramel and clear honey. Improves mouthfeel and head without darkening a beer much, so it suits pale ales and blondes.',
    tags: ['crystal', 'caramel', 'honey', 'light', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p32/CRYSTAL_20.html',
  }),
  craftMalt({
    id: 'sugar-creek-crystal-40',
    name: 'Crystal 40',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Dark orange with hints of red, and caramel and toffee to match. The middle grade and the most generally useful: five to ten percent rounds almost any amber or pale ale.',
    tags: ['crystal', 'caramel', 'toffee', 'amber', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p33/CRYSTAL_40.html',
  }),
  craftMalt({
    id: 'sugar-creek-crystal-60',
    name: 'Crystal 60',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Deeper again: burnt sugar, dark toffee and the first hint of dried fruit. Where an English bitter or a brown ale gets its color and much of its sweetness.',
    tags: ['crystal', 'caramel', 'english', 'brown-ale', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p34/CRYSTAL_60.html',
  }),
  craftMalt({
    id: 'sugar-creek-crystal-80',
    name: 'Crystal 80',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The step past Crystal 60: dark toffee and dried fruit with the first real raisin note, and enough color to take a beer deep amber on its own. Ten percent is a lot in most grists.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p35/CRYSTAL_80.html',
  }),
  craftMalt({
    id: 'sugar-creek-dark-crystal',
    name: 'Dark Crystal',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The dark end of the crystal range: raisin, fig and dark treacle. Five percent is plenty in most beers; more and the sweetness starts to sit on top of the finish.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-crystal-rye',
    name: 'Crystal Rye',
    maker: 'Sugar Creek Malt Co.',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 75,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Rye stewed and crystallized rather than barley, and it tastes like it: rye spice over pumpernickel, raisin and date. A small addition changes the character of a beer more than the color.',
    tags: ['crystal', 'rye', 'spice', 'pumpernickel', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p38/CRYSTAL_RYE.html',
  }),
  craftMalt({
    id: 'sugar-creek-biscuit',
    name: 'Biscuit Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Toasted rather than roasted: digestive biscuit and warm bread crust, with none of the bitterness a chocolate malt brings. Adds bakery depth to a pale or amber beer.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p45/BISCUIT_MALT.html',
  }),
  craftMalt({
    id: 'sugar-creek-brown',
    name: 'Brown Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Dark toasted bread, light chocolate and a suggestion of coffee. The malt an English brown ale and a historical porter are built around, and drier than a crystal of the same color.',
    tags: ['roasted', 'brown', 'porter', 'english', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p68/BROWNMALT.html',
  }),
  craftMalt({
    id: 'sugar-creek-pale-chocolate',
    name: 'Pale Chocolate',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Chocolate malt taken less far: cocoa and light coffee without the sharp edge of the full roast. Gives a porter its color with a softer finish.',
    tags: ['roasted', 'chocolate', 'porter', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p25/PALE_CHOCOLATE.html',
  }),
  craftMalt({
    id: 'sugar-creek-chocolate',
    name: 'Chocolate Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The house chocolate roast: dark cocoa, espresso and a clean bitter finish. Three to eight percent in a porter or stout.',
    tags: ['roasted', 'chocolate', 'porter', 'stout', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p26/CHOCOLATE_MALT.html',
  }),
  craftMalt({
    id: 'sugar-creek-chocolate-rye',
    name: 'Chocolate Rye',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Rye roasted to chocolate depth, which keeps the rye spice underneath the cocoa. A distinctive way to darken a roggenbier or add complexity to a stout.',
    tags: ['roasted', 'chocolate', 'rye', 'spice', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p28/CHOCOLATE_RYE.html',
  }),
  craftMalt({
    id: 'sugar-creek-black',
    name: 'Black Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Taken to the end of the roast: dry, sharp and almost ashy on its own, and exactly what a dry Irish stout needs for color and bite. Two to five percent is usually enough.',
    tags: ['roasted', 'black', 'stout', 'dry', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p27/BLACK_MALT.html',
  }),
  craftMalt({
    id: 'sugar-creek-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Unmalted barley roasted deep black. The signature of a dry Irish stout: coffee, a dry astringent bite and the tan head that comes with it.',
    tags: ['roasted', 'barley', 'stout', 'irish', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p31/ROASTED_BARLEY.html',
  }),
  craftMalt({
    id: 'sugar-creek-roasted-wheat',
    name: 'Roasted Wheat',
    maker: 'Sugar Creek Malt Co.',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 425,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Slightly darker than the roasted barley and noticeably smoother, because wheat is huskless and contributes none of the husk astringency. Color and roast for a stout that should stay soft.',
    tags: ['roasted', 'wheat', 'stout', 'smooth', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-rauchmalz',
    name: 'Rauchmalz',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Beechwood-smoked malt in the Bamberg manner, floor-malted in Indiana. Clean campfire smoke rather than bacon; it can be the whole grist of a rauchbier or season anything at 10–30 %.',
    tags: ['smoked', 'beechwood', 'rauchbier', 'base', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p59/German_Rauchmalz.html',
  }),
  craftMalt({
    id: 'sugar-creek-cherrywood-smoked',
    name: 'Cherrywood Smoked Malt',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Smoked over cherry wood: sweeter and fruitier than beech, with a rounder finish. Good in a smoked porter or an amber where beech would read as too austere.',
    tags: ['smoked', 'cherrywood', 'base', 'craft-malt', 'indiana', 'floor-malted'],
    url: 'http://www.sugarcreekmalt.com/store/p71/CHERRY.html',
  }),
  craftMalt({
    id: 'sugar-creek-white-oak-smoked-red-wheat',
    name: 'White Oak Smoked Red Wheat',
    maker: 'Sugar Creek Malt Co.',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Red wheat smoked over white oak, which is closer to a spirits barrel than to a campfire: vanilla and light tannin behind the smoke. Unusual and worth it in a smoked wheat beer.',
    tags: ['smoked', 'white-oak', 'wheat', 'craft-malt', 'indiana', 'floor-malted'],
  }),
  craftMalt({
    id: 'sugar-creek-bloody-butcher-corn',
    name: 'Bloody Butcher Malted Corn',
    maker: 'Sugar Creek Malt Co.',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 10,
    colorLovibond: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'A red heirloom dent corn, malted rather than flaked. Sweeter and far more characterful than commodity maize, with a faint nuttiness the flaked product does not have.',
    tags: [
      'corn',
      'maize',
      'heirloom',
      'adjunct',
      'gluten-free',
      'craft-malt',
      'indiana',
      'floor-malted',
    ],
    url: 'http://www.sugarcreekmalt.com/store/p46/BLOODY_BUTCHER_CORN.html',
  }),
  craftMalt({
    id: 'sugar-creek-oaxacan-green-corn',
    name: 'Oaxacan Green Corn',
    maker: 'Sugar Creek Malt Co.',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 77,
    moisturePercent: 10,
    colorLovibond: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'An heirloom Oaxacan green corn, malted. Grassy and sweet with a distinctive earthiness; a corn to build a beer around rather than to lighten one with.',
    tags: [
      'corn',
      'maize',
      'heirloom',
      'adjunct',
      'gluten-free',
      'craft-malt',
      'indiana',
      'floor-malted',
    ],
    url: 'http://www.sugarcreekmalt.com/store/p48/OAXACAN_GREEN_CORN_%5BMALTED%5D.html',
  }),

  // ---------------------------------------------------------------------------
  // Two Track Malting — Bismarck, North Dakota
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'two-track-badlands-pilsner',
    name: 'Badlands Pilsner',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Estate Brewski 2-row, a North Dakota State University craft variety grown only on the house farms. Delicate honey-malt flavor at 1.7 °L; plump kernels lautering faster than older six-row.',
    tags: ['base', 'pilsner', 'craft-malt', 'north-dakota', 'estate', 'brewski'],
    url: 'https://twotrackmalting.com/products/badlands-pilsner/',
  }),
  craftMalt({
    id: 'two-track-okefenokee-pale',
    name: 'Okefenokee Pale',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Brewski 2-row pale named for a farm pond that never dried up. Gentle sweetness, biscuit and a hint of nut — the everyday Field to Pint base for ale and lager.',
    tags: ['base', 'pale', 'craft-malt', 'north-dakota', 'estate', 'brewski'],
    url: 'https://twotrackmalting.com/products/okefenokee-pale/',
  }),
  craftMalt({
    id: 'two-track-astro-white-wheat',
    name: 'Astro White Wheat',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Hard spring wheat bred for malting and grown only on the house farms. Clean bread-dough aroma and a full-bodied wheat flavor for hefeweizen, wit and wheat ale.',
    tags: ['base', 'wheat', 'craft-malt', 'north-dakota', 'estate', 'head-retention'],
    url: 'https://twotrackmalting.com/products/astro-white-wheat/',
  }),
  craftMalt({
    id: 'two-track-medora-pale-ale',
    name: 'Medora Pale Ale',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned a step darker than Okefenokee, with honeyed malt depth for pale ale, mild, ESB and amber. Named for the Badlands town at the park gate.',
    tags: ['base', 'pale-ale', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://twotrackmalting.com/products/pale-ale-malt/',
  }),
  craftMalt({
    id: 'two-track-dakota-vienna',
    name: 'Dakota Vienna',
    maker: 'Two Track Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Highly modified Vienna kilned until the malthouse smells of nuts. Clean malt-sweetness with honey and almond; self-converting as a whole grist or a seasoning.',
    tags: ['kilned', 'vienna', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://twotrackmalting.com/products/dakota-vienna/',
  }),
  craftMalt({
    id: 'two-track-denhoff-munich',
    name: 'Denhoff Munich',
    maker: 'Two Track Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4,
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'First specialty malt from the Bismarck house: sweet toast, caramel and a dark-chocolate finish. Named for the town by the Stober family farm.',
    tags: ['kilned', 'munich', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://twotrackmalting.com/products/denhoff-munich/',
  }),
  craftMalt({
    id: 'two-track-bakken-60',
    name: 'Bakken 60',
    maker: 'Two Track Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 55,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Mid-color crystal from estate Brewski barley: caramel, toffee and mild burnt sugar. Five to ten percent in amber, brown and darker lager.',
    tags: ['crystal', 'caramel', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://twotrackmalting.com/products/bakken-60-caramel/',
  }),
  craftMalt({
    id: 'two-track-field-to-spirit',
    name: 'Field to Spirit',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 180,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lightly kilned conversion malt for craft distillers: high diastatic power, clean grain sweetness, and enough enzyme to convert unmalted North Dakota corn and rye.',
    tags: ['base', 'distillers', 'craft-malt', 'north-dakota', 'estate', 'high-enzyme'],
    url: 'https://twotrackmalting.com/products/field-to-spirit-distillers-conversion-malt/',
  }),
  craftMalt({
    id: 'two-track-maltese-ale',
    name: 'Maltese Ale Malt',
    maker: 'Two Track Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'An English mild-style ale malt: sweet and grainy, bready and cracker-like, and grain-forward in a way a modern pale malt is not. What a mild or a best bitter wants underneath it.',
    tags: ['base', 'ale', 'english', 'mild', 'bitter', 'craft-malt', 'north-dakota'],
    url: 'https://twotrackmalting.com/maltese-ale-malt/',
  }),
  craftMalt({
    id: 'two-track-red-river-caramel',
    name: 'Red River Caramel',
    maker: 'Two Track Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The lightest of the house caramels: honey, mild caramel and a delicate toffee. Rounds a pale ale or a red without darkening it much.',
    tags: ['crystal', 'caramel', 'honey', 'light', 'amber', 'craft-malt', 'north-dakota'],
    url: 'https://twotrackmalting.com/red-river-caramel-20-malt/',
  }),
  craftMalt({
    id: 'two-track-bakken-88',
    name: 'Bakken 88',
    maker: 'Two Track Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 88,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'The dark end of the Bakken pair, running deep amber to mahogany: rich caramel, toffee and a mild roasted-sugar note. For brown ales, porters and stouts.',
    tags: ['crystal', 'caramel', 'dark', 'toffee', 'porter', 'craft-malt', 'north-dakota'],
    url: 'https://twotrackmalting.com/products/bakken-88-caramel/',
  }),

  // ---------------------------------------------------------------------------
  // ND Malting and Hops — Williston, North Dakota
  //
  // Farm maltsters: the barley is grown on their own fields at Ray and Zahl, in
  // the north-west corner of the state, and malted at Williston (they quote up
  // to 30 t a week). Every product has a Norse name, and the names below are
  // exactly as the shop prints them.
  //
  // The figures come from their own certificate-of-analysis sheet,
  // `wp-content/uploads/2026/04/COAs_April_2026.pdf`, which is embedded on the
  // /coas/ page. It is lot data, not a typical-analysis sheet, so each row is
  // the arithmetic mean of every lot listed for that product: eight lots of
  // Paradise Pilsner, two each of Longship, Warrior White Wheat and Kings
  // Crystal 60, and one each of Mjolnir and War Hammer. Colour is published in
  // °SRM and stored here as °L unconverted. Extract is the fine-grind column
  // and DP the °L column. Barley is ND Genesis throughout and the wheat is
  // Brundage.
  //
  // Things a future editor will trip on:
  // - The COA heads Longship "Longboat Distillers Malt". The shop, the product
  //   page and the lot prefix (LDM) all say Longship, so Longship it is.
  // - The COA also lists a Crystal 80 lot (KCM-03-25, 107 °SRM). The shop does
  //   not sell a Crystal 80, so there is no row for it.
  // - The pale malt (2.1 °SRM) came out paler than the pilsner (2.5 °SRM mean).
  //   That is what the sheets say. One War Hammer lot is not enough to call it a
  //   typo.
  // - Valkyries Vienna and Chieftain Chit have no lot on the sheet, so those two
  //   rows (and the unmalted barley) are class-typical and say so. Only their
  //   names, the chit's 5–15 % dose and the flavor notes come from the maker.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'nd-malting-paradise-pilsner',
    name: 'Paradise Pilsner Malt',
    maker: 'ND Malting and Hops',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82.2,
    moisturePercent: 4.8,
    colorLovibond: 2.5,
    diastaticPowerLintner: 166,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'North Dakota Genesis barley malted on the grower’s own farm, clean and lightly sweet. Across eight published lots it averaged 2.5 °L with ample enzyme, so it carries a lager or session beer as the whole grist.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://ndmaltingandhops.com/product/paradise-pilsner-malt/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-war-hammer-pale',
    name: 'War Hammer Pale Malt',
    maker: 'ND Malting and Hops',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.3,
    moisturePercent: 5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 205,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house pale ale malt: a clean, grain-forward base with a little sweetness, meant for pale ales and IPAs where the hops lead. The published lot is very pale and very enzymatic, so it will convert a generous adjunct fraction.',
    tags: ['base', 'ale', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://ndmaltingandhops.com/product/war-hammer-pale-malt/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-longship-distillers',
    name: 'Longship Distillers Malt',
    maker: 'ND Malting and Hops',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.9,
    moisturePercent: 6,
    colorLovibond: 2.3,
    diastaticPowerLintner: 203,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned gently to keep its enzymes, around 200 °L diastatic power, for whiskey mash bills with unmalted grain in them. Brewers can use it the same way to convert a large share of corn, rice or raw barley.',
    tags: ['base', 'distilling', 'high-enzyme', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://ndmaltingandhops.com/product/longship-distillers-malt/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-warrior-white-wheat',
    name: 'Warrior White Wheat Malt',
    maker: 'ND Malting and Hops',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 6.4,
    colorLovibond: 2.7,
    diastaticPowerLintner: 196,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted Brundage white wheat, soft gold with a creamy mouthfeel and a delicate sweetness. For hefeweizen, witbier and any ale that wants more body and foam; use rice hulls once it passes about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'north-dakota', 'head-retention'],
    url: 'https://ndmaltingandhops.com/product/warrior-white-wheat-malt/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-mjolnir-light-munich',
    name: 'Mjolnir Light Munich Malt',
    maker: 'ND Malting and Hops',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 82.8,
    moisturePercent: 3,
    colorLovibond: 6.6,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Munich at 6.6 °L: mildly toasty, with brown sugar, toffee and toasted cereal. It keeps enough enzyme to convert itself, so use 10–30 % in an amber lager or make it the base of a malt-forward bock.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://ndmaltingandhops.com/product/mjolnir-light-munich-malt/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-kings-crystal-60',
    name: 'Kings Crystal Malt 60',
    maker: 'ND Malting and Hops',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 49.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Toffee, toasted biscuit and dried fruit such as raisin and fig, with a slight dark-caramel bite to balance it. Sold as a 60 but published at about 50 °L, so it runs a little lighter than the name. Five to fifteen percent in ambers, ESBs and bocks.',
    tags: ['crystal', 'caramel', 'toffee', 'craft-malt', 'north-dakota'],
    url: 'https://ndmaltingandhops.com/product/kings-crystal-malt-60/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'nd-malting-valkyries-vienna',
    name: 'Valkyries Vienna Malt',
    maker: 'ND Malting and Hops',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Toasted cereal with a hint of honey-roasted nut, plus some caramel and cracker sweetness. All values shown are class-typical because the maker’s analysis sheet has no Vienna lot; use it as the whole grist of a Vienna lager or as a quarter of an ale grist.',
    tags: ['kilned', 'vienna', 'lager', 'craft-malt', 'north-dakota', 'estate'],
    url: 'https://ndmaltingandhops.com/product/valkyries-vienna-malt/',
  }),
  craftMalt({
    id: 'nd-malting-chieftain-chit',
    name: 'Chieftain Chit Malt',
    maker: 'ND Malting and Hops',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Lightly germinated barley, kilned gently to keep its protein, for foam, body and haze. The maker recommends 5–15 % in hazy IPA, wheat beer and lager. All values shown are class-typical because no lot is published.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'north-dakota'],
    url: 'https://ndmaltingandhops.com/product/chieftain-chit-malt/',
  }),
  craftMalt({
    id: 'nd-malting-unmalted-barley',
    name: 'Unmalted Barley',
    maker: 'ND Malting and Hops',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 11,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Raw two-row barley from the same farms, for body and foam in stouts and farmhouse beers, or for a distiller’s mash bill. It has no enzymes and needs a mash with plenty of diastatic power. All values shown are class-typical because the maker lists it by name only.',
    tags: ['adjunct', 'unmalted', 'body', 'craft-malt', 'north-dakota'],
    url: 'https://ndmaltingandhops.com/product/unmalted-barley/',
  }),
];
